import { useState, useCallback } from 'react';
import {
  fetchCampaignOptions,
  fetchAmplifyCampaignName,
  fetchCampaigns,
  fetchCampaignById,
  createCampaign,
  updateCampaign,
  uploadCampaignCreatives,
  updateCampaignCreatives,
  addCampaignPocs,
  fetchCampaignPocs,
  fetchOptimizedBudgets,
} from '@/services/api';

// Shared helper to validate required campaign fields after mapping
const getMissingCampaignFields = (mappedData) => {
  const missingFields = [];
  if (!mappedData.campaign_name?.trim()) missingFields.push('campaign_name');
  if (!mappedData.brand?.trim()) missingFields.push('brand');
  if (!mappedData.region?.trim()) missingFields.push('region');
  if (!mappedData.campaign_type?.trim()) missingFields.push('campaign_type');
  if (!mappedData.campaign_objective?.trim()) missingFields.push('campaign_objective');
  if (!mappedData.start_date?.trim()) missingFields.push('start_date');
  if (!mappedData.end_date?.trim()) missingFields.push('end_date');
  if (!mappedData.role?.trim()) missingFields.push('role');
  if (!mappedData.gl_code?.trim()) missingFields.push('gl_code');
  if (!mappedData.cost_center?.trim()) missingFields.push('cost_center');
  if (!mappedData.bdr_email?.trim()) missingFields.push('bdr_email');
  if (!mappedData.budget_type?.trim()) missingFields.push('budget_type');
  return missingFields;
};

// Shared helper to normalize campaign create/update API errors into a message
const buildCampaignApiErrorMessage = (defaultMessage, err) => {
  let errorMessage = defaultMessage;

  if (err?.response) {
    const responseData = err.response.data;

    if (err.response.status === 422 && responseData?.detail && Array.isArray(responseData.detail)) {
      const validationErrors = responseData.detail
        .map(error => `${error.loc.join('.')}: ${error.msg}`)
        .join('; ');
      errorMessage = `Validation Error: ${validationErrors}`;
    } else {
      errorMessage = responseData?.message || responseData?.error || `Server error: ${err.response.status}`;
    }
  } else if (err?.message) {
    errorMessage = err.message;
  }

  return errorMessage;
};

// Shared helper to build FormData for creative upload/update flows
const buildCreativeFormData = (creativeData) => {
  // Extract and validate creative_type (mandatory)
  let creativeType = creativeData.creative_type || creativeData.creativeType;

  // Auto-adjust creative_type based on data provided
  if (creativeType === 'upload') {
    const hasFiles = creativeData.creatives && creativeData.creatives.length > 0;
    const hasNewDescription = (creativeData.new_creative_description || creativeData.newCreativeDescription)?.trim();

    // If no files but has description, change to 'request' type
    if (!hasFiles && hasNewDescription) {
      // console.log('Auto-converting creative_type from "upload" to "request" (no files, but has description)');
      creativeType = 'request';
    }
  }

  if (!creativeType || !['upload', 'existing', 'request'].includes(creativeType)) {
    throw new Error('creative_type is required and must be one of: upload, existing, request');
  }

  // Validate mandatory fields
  const adHeadline = creativeData.ad_headline || creativeData.adHeadline;
  if (!adHeadline || !adHeadline.trim()) {
    throw new Error('ad_headline is required');
  }

  const bodyCopy = creativeData.body_copy || creativeData.bodyCopy;
  if (!bodyCopy || !bodyCopy.trim()) {
    throw new Error('body_copy is required');
  }

  // Create FormData used by both create/update flows
  const formData = new FormData();

  // Add creative_type - ensure it's a string
  formData.append('creative_type', String(creativeType));

  // Add mandatory common fields - ensure they are strings
  formData.append('ad_headline', String(adHeadline.trim()));
  formData.append('body_copy', String(bodyCopy.trim()));

  // Add optional creative description
  const creativeDescription = creativeData.creative_description || creativeData.creativeDescription;
  if (creativeDescription && creativeDescription.trim()) {
    formData.append('creative_description', String(creativeDescription.trim()));
  }

  // Handle type-specific fields and validations
  if (creativeType === 'upload') {
    // Add creative files if provided
    if (creativeData.creatives && creativeData.creatives.length > 0) {
      creativeData.creatives.forEach((file) => {
        formData.append('creative_files', file);
      });
    }

    // Add new creative description if provided (optional for upload type)
    const newCreativeDescription = creativeData.new_creative_description ||
      creativeData.newCreativeDescription;

    if (newCreativeDescription && newCreativeDescription.trim()) {
      formData.append('new_creative_description', String(newCreativeDescription).trim());
    }
  }
  else if (creativeType === 'existing') {
    // Handle existing creative description
    const existingCreativeDescription = creativeData.existing_creative_description ||
      creativeData.existingCreativeDescription;

    if (!existingCreativeDescription || existingCreativeDescription.trim() === '') {
      // console.error('Missing existing creative description. CreativeData keys:', Object.keys(creativeData));
      // console.error('CreativeData values:', creativeData);
      throw new Error('existing_creative_description is required when creative_type is existing');
    }

    formData.append('existing_creative_description', String(existingCreativeDescription).trim());
  }
  else if (creativeType === 'request') {
    // Handle new creative description for request type
    const newCreativeDescription = creativeData.new_creative_description ||
      creativeData.newCreativeDescription;

    if (!newCreativeDescription || newCreativeDescription.trim() === '') {
      // console.error('Missing new creative description. CreativeData:', creativeData);
      throw new Error('new_creative_description is required when creative_type is request');
    }

    formData.append('new_creative_description', String(newCreativeDescription).trim());
  }

  // Append targeting fields if provided
  const minAge = creativeData.min_age || creativeData.minAge;
  const maxAge = creativeData.max_age || creativeData.maxAge;
  const ctaType = creativeData.cta_type || creativeData.ctaType;

  if (minAge !== undefined && minAge !== null && String(minAge).trim() !== '') {
    formData.append('min_age', String(minAge).trim());
  }

  if (maxAge !== undefined && maxAge !== null && String(maxAge).trim() !== '') {
    formData.append('max_age', String(maxAge).trim());
  }

  if (ctaType !== undefined && ctaType !== null && String(ctaType).trim() !== '') {
    formData.append('cta_type', String(ctaType).trim());
  }

  // Append additional_comment if provided
  const additionalComment = creativeData.additional_comment || creativeData.additionalComment;
  if (additionalComment !== undefined && additionalComment !== null && String(additionalComment).trim() !== '') {
    formData.append('additional_comment', String(additionalComment).trim());
  }

  return { formData, creativeType };
};

export const useCampaignAPI = () => {
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [campaignOptions, setCampaignOptions] = useState(null);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Fetch campaign options (dropdown data)
  const getCampaignOptions = useCallback(async () => {
    try {
      setLoading(true);
      clearError();
      
      const response = await fetchCampaignOptions();
      
      if (response.status) {
        setCampaignOptions(response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch campaign options');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Fetch Amplify campaign name validation
  const getAmplifyCampaignName = useCallback(async (region, brand, campaignName,budget_type) => {
    try {
      setLoading(true);
      clearError();
      
      // Validate required parameters
      if (!region || !brand || !campaignName || !budget_type) {
        throw new Error('Region, brand, campaign name, and budget type are required');
      }

      const response = await fetchAmplifyCampaignName(region, brand, campaignName, budget_type);

      // Check for success property instead of status
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch Amplify campaign name');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Map frontend form data to backend API format
  const mapCampaignDataForAPI = useCallback((campaignData) => {
    const mappedData = {};
    
    // Required fields - always include these
    mappedData.campaign_name = campaignData.campaignName || '';
    mappedData.brand = campaignData.brand || '';
    mappedData.region = campaignData.region || '';
    mappedData.campaign_type = campaignData.platform || '';
    mappedData.campaign_objective = campaignData.campaignObjective || '';
    mappedData.start_date = campaignData.startDate || '';
    mappedData.end_date = campaignData.endDate || '';
    mappedData.role = campaignData.role || '';
    
    // Required fields - GL Code, Cost Center, and BDR Email
    mappedData.gl_code = (campaignData.gl_code || campaignData.glCode || '').trim();
    mappedData.cost_center = (campaignData.cost_centre || campaignData.costCenter || '').trim();
    mappedData.bdr_email = (campaignData.bdrEmail || campaignData.bdr_email || '').trim();
    mappedData.budget_type = campaignData.budget_type || '';

    // Optional fields - only include if they have values
    if (campaignData.territory && campaignData.territory.trim()) mappedData.territory = campaignData.territory;
    if (campaignData.totalBudget && campaignData.totalBudget > 0) mappedData.total_budget = parseFloat(campaignData.totalBudget);
    if (campaignData.noOfPocs && campaignData.noOfPocs > 0) mappedData.no_of_pocs = parseInt(campaignData.noOfPocs);
    
    return mappedData;
  }, []);

  // Create a new campaign
  const createNewCampaign = useCallback(async (campaignData) => {
    try {
      setLoading(true);
      clearError();
      
      // Map frontend data to backend format
      const mappedData = mapCampaignDataForAPI(campaignData);
      
      // console.log('Original campaign data:', campaignData);
      // console.log('Mapped campaign data for API:', mappedData);
      
      // Validate required fields before sending
      const missingFields = getMissingCampaignFields(mappedData);
      if (missingFields.length > 0) {
        const errorMessage = `Missing required fields: ${missingFields.join(', ')}`;
        throw new Error(errorMessage);
      }
      
      const response = await createCampaign(mappedData);
      
      if (response.status) {
        return response.data;
      } else {
        const errorMessage = response.message || response.error || 'Failed to create campaign';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Campaign creation error details:', err);
      const errorMessage = buildCampaignApiErrorMessage('Failed to create campaign', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [clearError, mapCampaignDataForAPI]);

  // Upload campaign creatives (handles all three creative types)
  const uploadCreatives = useCallback(async (campaignId, creativeData) => {
    try {
      setLoading(true);
      clearError();
      
      // Build FormData and normalized creativeType
      const { formData, creativeType } = buildCreativeFormData(creativeData);
      
      // console.log('Creating new FormData object...');
      // console.log('FormData prototype:', Object.getPrototypeOf(formData));
      
      // console.log('Uploading creatives for campaign:', campaignId, 'Type:', creativeType);
      // console.log('Creative data input:', creativeData);
      
      // Ensure we're sending pure FormData (not JSON)
      // console.log('=== FORMDATA CONSTRUCTION VERIFICATION ===');
      // console.log('FormData object type:', formData.constructor.name);
      // console.log('Is FormData?', formData instanceof FormData);
      // console.log('FormData prototype check:', Object.prototype.toString.call(formData));
      
      // Verify all FormData entries are properly set
      // console.log('FormData contents:');
      const entries = [];
      for (let [key, value] of formData.entries()) {
        const logValue = value instanceof File ? `File: ${value.name} (${value.size} bytes)` : `String: "${value}"`;
        // console.log(`  ${key}: ${logValue}`);
        entries.push({ key, value: logValue });
      }
      // console.log('Total FormData entries:', entries.length);
      
      // Ensure we haven't accidentally converted to JSON anywhere
      if (formData instanceof FormData) {
        // console.log('✓ Confirmed: Sending as FormData (multipart/form-data)');
      } else {
        console.error('✗ ERROR: Data is not FormData!', typeof formData);
        throw new Error('Data must be sent as FormData, not JSON');
      }
      // console.log('=== END FORMDATA VERIFICATION ===');
      
      const response = await uploadCampaignCreatives(campaignId, formData);
      
      // console.log('API Response:', response);
      
      if (response.status) {
        return response.data;
      } else {
        console.error('API returned unsuccessful response:', response);
        throw new Error(response.message || 'Failed to upload creatives');
      }
    } catch (err) {
      console.error('Creative upload error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Add POCs to campaign (supports both object and array formats)
  const addPocsToCampaign = useCallback(async (campaignId, pocsData, additionalParams = {}) => {
    try {
      setLoading(true);
      clearError();
      
      // Handle both input formats:
      // 1. Array format: [poc1, poc2, ...]
      // 2. Object format: {pocs: [poc1, poc2, ...]}
      let pocItems;
      
      if (Array.isArray(pocsData)) {
        pocItems = pocsData;
      } else if (pocsData.pocs && Array.isArray(pocsData.pocs)) {
        pocItems = pocsData.pocs;
      } else {
        throw new Error('Invalid POC data format. Expected array or object with pocs property');
      }

      // Validate that POCs array is not empty
      if (!pocItems || pocItems.length === 0) {
        throw new Error('At least one POC is required');
      }

      // Validate that all POCs have budgets set and they're greater than 0
      const pocsWithoutBudget = pocItems.filter(poc => {
        const budget = poc.poc_budget;
        return budget === null || budget === undefined || budget === '' || budget === 0 || isNaN(budget);
      });

      if (pocsWithoutBudget.length > 0) {
        const errorMessage = `${pocsWithoutBudget.length} POC${pocsWithoutBudget.length > 1 ? 's' : ''} ${pocsWithoutBudget.length > 1 ? 'have' : 'has'} missing or zero budget. All POCs must have a valid budget greater than 0.`;
        throw new Error(errorMessage);
      }

      // Validate that all budgets are positive numbers
      const pocsWithInvalidBudget = pocItems.filter(poc => {
        const budget = poc.poc_budget;
        return typeof budget === 'number' && budget <= 0;
      });

      if (pocsWithInvalidBudget.length > 0) {
        const errorMessage = `${pocsWithInvalidBudget.length} POC${pocsWithInvalidBudget.length > 1 ? 's' : ''} ${pocsWithInvalidBudget.length > 1 ? 'have' : 'has'} invalid budget. All POC budgets must be greater than 0.`;
        throw new Error(errorMessage);
      }

      // Validate that all budgets are valid numbers (not NaN)
      const pocsWithNaNBudget = pocItems.filter(poc => {
        const budget = poc.poc_budget;
        return typeof budget !== 'number' || isNaN(budget);
      });

      if (pocsWithNaNBudget.length > 0) {
        const errorMessage = `${pocsWithNaNBudget.length} POC${pocsWithNaNBudget.length > 1 ? 's' : ''} ${pocsWithNaNBudget.length > 1 ? 'have' : 'has'} non-numeric budget. All POC budgets must be valid numbers.`;
        throw new Error(errorMessage);
      }

      // Validate that all POC items have the correct campaign_id
      pocItems.forEach((poc, index) => {
        if (poc.campaign_id && poc.campaign_id !== parseInt(campaignId)) {
          throw new Error(`POC at index ${index} has campaign_id ${poc.campaign_id} that doesn't match ${campaignId}`);
        }
      });

      // console.log(`Adding ${pocItems.length} POCs to campaign ${campaignId}`);
      // console.log('POC budgets validation passed - all budgets > 0');
      // console.log('Additional params:', additionalParams);
      
      // Prepare the request payload with additional parameters
      // Send enum keys (BPP or BO) to match backend BudgetAllocationModeEnum
      const requestPayload = {
        poc_data: pocItems,
        budget_allocation_mode: additionalParams.budget_allocation_mode || 'BPP',
        budget_per_poc: additionalParams.budget_per_poc !== undefined ? parseInt(additionalParams.budget_per_poc) : -1
      };
      
      // console.log('Request payload:', requestPayload);
      
      // Send payload to API
      const response = await addCampaignPocs(campaignId, requestPayload);
      
      if (response.status) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to add POCs to campaign');
      }
    } catch (err) {
      console.error('POC addition error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Get list of campaigns by region and brand
  const getCampaigns = useCallback(async (region, brand) => {
    try {
      setLoading(true);
      clearError();
      
      // Get BDR email from sessionStorage (CON-3585)
      let bdrEmail = null;
      try {
        const authUser = sessionStorage.getItem('auth_user');
        if (authUser) {
          const user = JSON.parse(authUser);
          bdrEmail = user.email || null;
          // console.log('Fetching campaigns for BDR:', bdrEmail);
        }
      } catch (err) {
        console.warn('Could not retrieve user email from sessionStorage:', err);
      }
      
      const response = await fetchCampaigns(region, brand, bdrEmail);
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch campaigns');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Get campaign details by ID
  const getCampaignById = useCallback(async (campaignId) => {
    try {
      setLoading(true);
      clearError();
      
      const response = await fetchCampaignById(campaignId);
      
      if (response.success) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch campaign details');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Update an existing campaign
  const updateExistingCampaign = useCallback(async (campaignId, campaignData) => {
    try {
      setLoading(true);
      clearError();
      
      // Map frontend data to backend format
      const mappedData = mapCampaignDataForAPI(campaignData);
      
      // console.log('Original campaign data for update:', campaignData);
      // console.log('Mapped campaign data for API update:', mappedData);
      
      // Validate required fields before sending
      const missingFields = getMissingCampaignFields(mappedData);
      if (missingFields.length > 0) {
        const errorMessage = `Missing required fields: ${missingFields.join(', ')}`;
        throw new Error(errorMessage);
      }
      
      const response = await updateCampaign(campaignId, mappedData);
      
      if (response.status) {
        return response.data;
      } else {
        const errorMessage = response.message || response.error || 'Failed to update campaign';
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error('Campaign update error details:', err);
      const errorMessage = buildCampaignApiErrorMessage('Failed to update campaign', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [clearError, mapCampaignDataForAPI]);

  // Update campaign creatives (PUT request for existing campaigns)
  const updateCreativesForCampaign = useCallback(async (campaignId, creativeData) => {
    try {
      setLoading(true);
      clearError();
      
      // Build FormData and normalized creativeType
      const { formData, creativeType } = buildCreativeFormData(creativeData);

      // console.log('Creating FormData for creative update...');

      // console.log('Updating creatives for campaign:', campaignId, 'Type:', creativeType);
      
      // Verify FormData
      // console.log('FormData verification for update:');
      for (let [key, value] of formData.entries()) {
        const logValue = value instanceof File ? `File: ${value.name}` : value;
        // console.log(`  ${key}: ${logValue}`);
      }
      
      const response = await updateCampaignCreatives(campaignId, formData);
      
      if (response.status) {
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update creatives');
      }
    } catch (err) {
      console.error('Creative update error:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Get campaign POCs (selected POCs for a campaign)
  const getCampaignPocs = useCallback(async (campaignId, skip = 0, limit = 100) => {
    try {
      setLoading(true);
      clearError();
      
      if (!campaignId) {
        throw new Error('Campaign ID is required');
      }
      
      // console.log(`Fetching POCs for campaign ${campaignId} (skip: ${skip}, limit: ${limit})`);
      
      const response = await fetchCampaignPocs(campaignId, skip, limit);
      
      if (response.status) {
        // console.log('Successfully fetched campaign POCs:', response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to fetch campaign POCs');
      }
    } catch (err) {
      console.error('Error fetching campaign POCs:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);

  // Fetch optimized budgets for POCs
  const getOptimizedBudgets = useCallback(async (payload) => {
    try {
      setLoading(true);
      clearError();
      
      // Validate payload structure
      if (!payload || !payload.pocs || !Array.isArray(payload.pocs)) {
        throw new Error('Invalid payload: pocs array is required');
      }
      
      if (payload.pocs.length === 0) {
        throw new Error('Cannot optimize budgets for empty POC list');
      }
      
      // Validate required fields in payload
      const requiredFields = ['region', 'brand', 'campaign_id', 'start_date', 'end_date'];
      const missingFields = requiredFields.filter(field => !payload[field]);
      
      if (missingFields.length > 0) {
        console.warn('Missing optional fields in budget optimization payload:', missingFields);
      }
      
      // console.log('Fetching optimized budgets for payload:', payload);
      
      const response = await fetchOptimizedBudgets(payload);
      
      // Normalize response format
      let optimizedList = Array.isArray(response) ? response : (response?.data || []);
      
      if (!Array.isArray(optimizedList)) {
        throw new Error('Invalid response format from budget optimization API');
      }
      
      // Validate response contains required fields
      const validatedList = optimizedList.map((item, index) => {
        if (!item.poc_id) {
          console.error(`Invalid item at index ${index}: missing poc_id`, item);
          throw new Error(`Invalid response: item at index ${index} missing poc_id`);
        }
        
        return {
          poc_id: item.poc_id,
          poc_name: item.poc_name || '',
          pred_budget: item.pred_budget || 0,
          Overlapping: item.Overlapping || '-',
          // Include any additional fields from response
          ...item
        };
      });
      
      // console.log('Successfully fetched optimized budgets:', validatedList);
      
      return validatedList;
    } catch (err) {
      console.error('Error fetching optimized budgets:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [clearError]);
  
  // Apply optimized budgets to POCs - modular helper function
  const applyOptimizedBudgetsToPOCs = useCallback((pocs, optimizedBudgets) => {
    if (!Array.isArray(pocs) || !Array.isArray(optimizedBudgets)) {
      console.error('Invalid input to applyOptimizedBudgetsToPOCs');
      return { updatedPocs: pocs, budgetMap: {} };
    }
    
    // Create budget map for easy lookup
    const budgetMap = {};
    optimizedBudgets.forEach(({ poc_id, pred_budget }) => {
      budgetMap[poc_id] = pred_budget;
    });
    
    // Update POCs with optimized budgets and overlapping data
    const updatedPocs = pocs.map(poc => {
      const optimizedData = optimizedBudgets.find(r => r.poc_id === poc.poc_id);
      if (optimizedData) {
        return {
          ...poc,
          poc_budget: optimizedData.pred_budget,
          Overlapping: optimizedData.Overlapping
        };
      }
      return poc;
    });
    
    return { updatedPocs, budgetMap };
  }, []);

  return {
    // State
    loading,
    error,
    campaignOptions,
    
    // Actions
    clearError,
    getCampaignOptions,
    getAmplifyCampaignName,
    getCampaigns,
    getCampaignById,
    getCampaignPocs,
    createNewCampaign,
    updateExistingCampaign,
    uploadCreatives,
    updateCreativesForCampaign,
    addPocsToCampaign,
    getOptimizedBudgets,
    applyOptimizedBudgetsToPOCs,
    
    // Utilities
    mapCampaignDataForAPI,
  };
};
